import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  User, 
  Mail, 
  Building2, 
  ShieldAlert, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  Phone, 
  Globe, 
  Cloud, 
  CreditCard, 
  History,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { db } from '@/src/lib/firebase';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '@/src/lib/firestore-errors';
import { useNavigate } from 'react-router-dom';
import { trackLead } from '@/src/lib/analytics';
// Note: AI qualification is handled via /api/qualify-lead for security

interface FormData {
  businessName: string;
  name: string;
  email: string;
  phone: string;
  industry: string;
  businessSize: string;
  website: string;
  cloudProvider: string;
  processPayments: string; // Using string for radio/select
  previousIncidents: string;
  securityConcern: string;
}

export default function ClientIntakeForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    name: '',
    email: '',
    phone: '',
    industry: '',
    businessSize: '',
    website: '',
    cloudProvider: '',
    processPayments: '',
    previousIncidents: '',
    securityConcern: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const navigate = useNavigate();

  const totalSteps = 4;

  const validateUrl = (url: string) => {
    if (!url) return true;
    const pattern = new RegExp('^(https?:\\/\\/)?'+ 
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ 
      '((\\d{1,3}\\.){3}\\d{1,3}))'+ 
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ 
      '(\\?[;&a-z\\d%_.~+=-]*)?'+ 
      '(\\#[-a-z\\d_]*)?$','i');
    return !!pattern.test(url);
  };

  const handleNext = () => {
    if (step === 2) {
      if (!validateUrl(formData.website)) {
        setWebsiteError('Please enter a valid website URL');
        return;
      } else {
        setWebsiteError('');
      }
    }
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Final validation check
    if (!validateUrl(formData.website)) {
      setStep(2); // Jump back to step 2
      setWebsiteError('Please enter a valid website URL before submitting');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Production Pipeline: Unified backend handling
      const response = await fetch('/api/automate-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          processPayments: formData.processPayments === 'yes',
          type: 'audit_intake',
          source: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Automation sequence failed');

      // Track lead submission
      trackLead({
        business_name: formData.businessName,
        industry: formData.industry,
        cloud_provider: formData.cloudProvider
      });

      setIsSuccess(true);
      setTimeout(() => {
        navigate('/thank-you');
      }, 2000);
    } catch (error) {
      console.error("Critical submission error:", error);
      alert("System error. Please contact us directly at +1 202 630 6862.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <AnimatePresence mode="wait">
        {!isSuccess ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass-card rounded-3xl p-6 md:p-10 border-accent/10 relative overflow-hidden"
          >
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-white/5">
              <motion.div
                className="h-full bg-accent"
                initial={{ width: '0%' }}
                animate={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>

            <div className="flex justify-between items-center mb-8">
              <div className="space-y-1">
                <p className="text-[9px] font-bold text-accent uppercase tracking-[0.2em]">Step {step} of {totalSteps}</p>
                <h3 className="text-xl font-display font-bold tracking-tight">
                  {step === 1 && "Identity & Contact"}
                  {step === 2 && "Business Profile"}
                  {step === 3 && "Technical Infrastructure"}
                  {step === 4 && "Security Context"}
                </h3>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-medium bg-white/5 px-2.5 py-1 rounded-full border border-white/10">
                <Clock className="w-3 h-3 text-accent" />
                3 mins
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Business Name</label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          required
                          placeholder="Acme Corp"
                          value={formData.businessName}
                          onChange={(e) => updateFormData('businessName', e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Contact Name</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            required
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={(e) => updateFormData('name', e.target.value)}
                            className="pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            required
                            type="tel"
                            placeholder="(555) 000-0000"
                            value={formData.phone}
                            onChange={(e) => updateFormData('phone', e.target.value)}
                            className="pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Work Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          required
                          type="email"
                          placeholder="john@acme.com"
                          value={formData.email}
                          onChange={(e) => updateFormData('email', e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Industry</label>
                      <select
                        required
                        value={formData.industry}
                        onChange={(e) => updateFormData('industry', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 h-12 rounded-xl px-4 focus:border-accent/50 outline-none text-foreground appearance-none text-sm"
                      >
                        <option value="" disabled className="bg-background">Select Industry</option>
                        <option value="retail" className="bg-background">Retail</option>
                        <option value="ecommerce" className="bg-background">E-commerce</option>
                        <option value="saas" className="bg-background">Technology / SaaS</option>
                        <option value="other" className="bg-background">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Number of Employees</label>
                      <div className="grid grid-cols-2 gap-3">
                        {['1-50', '51-200', '201-1000', '1000+'].map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => updateFormData('businessSize', size)}
                            className={`h-12 rounded-xl border transition-all font-medium text-sm ${
                              formData.businessSize === size
                                ? 'bg-accent border-accent text-background'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Website / Domain</label>
                      <div className="relative">
                        <Globe className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${websiteError ? 'text-red-500' : 'text-muted-foreground'}`} />
                        <Input
                          required
                          placeholder="www.acme.com"
                          value={formData.website}
                          onChange={(e) => {
                            updateFormData('website', e.target.value);
                            if (websiteError) setWebsiteError('');
                          }}
                          className={`pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50 ${websiteError ? 'border-red-500/50 focus:border-red-500' : ''}`}
                        />
                      </div>
                      {websiteError && (
                        <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-[10px] text-red-500 font-bold ml-1 flex items-center gap-1"
                        >
                          <AlertCircle className="w-3 h-3" /> {websiteError}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Primary Cloud Provider</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { id: 'aws', label: 'AWS', icon: Cloud },
                          { id: 'azure', label: 'Azure', icon: Cloud },
                          { id: 'gcp', label: 'GCP', icon: Cloud },
                          { id: 'hybrid', label: 'Hybrid', icon: Cloud },
                        ].map((provider) => (
                          <button
                            key={provider.id}
                            type="button"
                            onClick={() => updateFormData('cloudProvider', provider.id)}
                            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-all ${
                              formData.cloudProvider === provider.id
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            <provider.icon className="w-5 h-5" />
                            <span className="font-bold text-[10px] uppercase tracking-widest">{provider.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Do you process payments?</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { id: 'yes', label: 'Yes', icon: CreditCard },
                          { id: 'no', label: 'No', icon: ShieldAlert },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => updateFormData('processPayments', opt.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                              formData.processPayments === opt.id
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            <opt.icon className="w-4 h-4" />
                            <span className="font-bold text-sm">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Any previous security incidents?</label>
                      <div className="relative">
                        <History className="absolute left-4 top-3 w-4 h-4 text-muted-foreground" />
                        <textarea
                          placeholder="Briefly describe any past breaches or incidents (if any)..."
                          value={formData.previousIncidents}
                          onChange={(e) => updateFormData('previousIncidents', e.target.value)}
                          className="w-full pl-10 pt-3 bg-white/5 border border-white/10 h-24 rounded-xl focus:border-accent/50 outline-none text-foreground resize-none text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Primary Security Concern</label>
                      <div className="relative">
                        <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          required
                          placeholder="e.g. Data breach, Compliance, Ransomware"
                          value={formData.securityConcern}
                          onChange={(e) => updateFormData('securityConcern', e.target.value)}
                          className="pl-10 bg-white/5 border-white/10 h-12 rounded-xl focus:border-accent/50"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex gap-3 pt-2">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="h-12 px-6 rounded-xl border-white/10 hover:bg-white/5"
                  >
                    Back
                  </Button>
                )}
                {step < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={
                      (step === 1 && (!formData.businessName || !formData.name || !formData.email || !formData.phone)) ||
                      (step === 2 && (!formData.industry || !formData.businessSize || !formData.website)) ||
                      (step === 3 && (!formData.cloudProvider || !formData.processPayments))
                    }
                    className="flex-grow h-12 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold"
                  >
                    Continue <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.securityConcern}
                    className="flex-grow h-12 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold"
                  >
                    {isSubmitting ? "Securing Data..." : "Submit Intake Profile"}
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-white/5">
              <div className="flex items-center justify-center gap-4 text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-bold">
                <span className="flex items-center gap-1.5"><ShieldAlert className="w-2.5 h-2.5 text-accent" /> Encrypted</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-2.5 h-2.5 text-accent" /> GDPR Compliant</span>
                <span className="flex items-center gap-1.5"><User className="w-2.5 h-2.5 text-accent" /> Confidential</span>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-3xl p-12 text-center space-y-6 border-accent/20"
          >
            <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center text-background mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-display font-bold tracking-tight">Request Received</h3>
              <p className="text-base text-muted-foreground font-normal max-w-md mx-auto">
                Your intelligence profile has been securely transmitted. Our team will review your submission and contact you with next steps.
              </p>
            </div>
            <div className="pt-6">
              <p className="text-[10px] text-accent uppercase tracking-[0.3em] font-bold mb-4">Quality Control in Progress</p>
              <div className="text-[9px] text-muted-foreground/60 uppercase tracking-widest leading-relaxed max-w-xs mx-auto">
                All requests are professionally reviewed. If your business is a good fit, we will reach out via the provided contact details.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
